import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Inject,
    Input,
    OnInit,
    Optional,
    Renderer2
} from '@angular/core';
import {merge, Subscription, timer} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {squash} from '../../../../common/rxjs/squash';
import {BUY_SIDE_CLASS, SELL_SIDE_CLASS} from '../../../../common/side-classes';
import {AccountService} from '../../../../components/accounts';
import {destroy$} from '../../../../components/destroyable';
import {WIDGET_UPDATES_GATING$} from '../../../../components/layout/widget/widget-updates-gating';
import {ModalService} from '../../../../components/modal';
import {OverlayService} from '../../../../components/overlay';
import {EXCHANGE_NAMING} from '../../../../enums/exchange';
import {Side} from '../../../../enums/side';
import {formatSecurityKey} from '../../../../models/security-key';
import {CellSizeObserverService} from '../../../../pages/dashboard/layout/cell/cell-size-observer.service';
import {IS_DETACHED_WIDGET} from '../../../../pages/widget-page/detached-widget-injection';
import {UserService} from '../../../../services/user.service';
import {DisplayQuote, DisplayQuoteFactory} from '../../../quotes-widget/display-quote.factory';
import {QuotesStreamingService} from '../../quotes-streaming';
import {Quote} from '../../quotes-streaming/quote';
import {WidgetOrderConfirmModalInfo} from './widget-order-confirm-modal-common';

interface SignNamingMap {
    POSITIVE: string;
    NEGATIVE: string;
}

const SIDE_BY_SIGN: SignNamingMap = {
    POSITIVE: 'components.widgets.positions-widget.side_long',
    NEGATIVE: 'components.widgets.positions-widget.side_short'
};

const CLASS_BY_SIDE_MAP = {
    [Side.SELL]:             SELL_SIDE_CLASS,
    [SIDE_BY_SIGN.NEGATIVE]: SELL_SIDE_CLASS,
    [Side.BUY]:              BUY_SIDE_CLASS,
    [SIDE_BY_SIGN.POSITIVE]: BUY_SIDE_CLASS
};

const WIDE_CLASS = '-wide';
const FULL_SIZE_CLASS = '-full-size';
const HIDDEN_CLASS = '-hidden';

@Component({
    selector: 'widget-order-confirm-modal',
    templateUrl: './widget-order-confirm-modal.component.html',
    styleUrls: ['./widget-order-confirm-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WidgetOrderConfirmModalComponent implements OnInit {

    @Input()
    public widgetOrderConfirmModalInfo: WidgetOrderConfirmModalInfo;

    @Input()
    @HostBinding(`class.${WIDE_CLASS}`)
    public isWide: boolean = false;

    @HostBinding(`class.${FULL_SIZE_CLASS}`)
    public isFullSize: boolean = false;

    @HostBinding(`class.${HIDDEN_CLASS}`)
    public isHidden: boolean = true; // Initially hide the modal before the full-size mode is not checked.

    public readonly destroy$ = destroy$();

    public displayQuote: DisplayQuote;

    public onConfirmCallback: () => void;

    public CLASS_BY_SIDE_MAP = CLASS_BY_SIDE_MAP;

    public side = Side;

    public supportExchangeTranslationParams: {exchangeName: string};

    /**
     * Tells whether Accounts page is allowed to the user's role
     */
    public readonly canManageAccounts = AccountService.canUserRoleManageAccounts(this.userService.user.role);

    constructor(
        @Optional()
        @Inject(IS_DETACHED_WIDGET)
        public isDetachedWidget: boolean,

        private elementRef: ElementRef,
        private modalService: ModalService,
        private overlayService: OverlayService,
        private quotesStreamingService: QuotesStreamingService,
        private cellSizeObserverService: CellSizeObserverService,
        private displayQuoteFactory: DisplayQuoteFactory,
        protected changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        private renderer: Renderer2
    ) {}

    public onCloseCallback: () => void = () => null;

    public ngOnInit(): void {
        this.createOverlay();

        if (this.widgetOrderConfirmModalInfo.isListenToQuotesEnabled) {
            this.listenToQuotes();
        }

        if (this.widgetOrderConfirmModalInfo.isExchangeNotAllowedToTrade) {
            const exchangeName = EXCHANGE_NAMING[this.widgetOrderConfirmModalInfo.security.securityExchange]
                || this.widgetOrderConfirmModalInfo.security.securityExchange;

            this.supportExchangeTranslationParams = {exchangeName};
        }

        this.listenToWidgetSize();
    }

    public confirm(): void {
        this.onConfirmCallback();
        this.close();
    }

    public close(): void {
        this.modalService.close(this);
        this.onCloseCallback();
    }

    public listenToQuotes(): Subscription {
        const securityKey = formatSecurityKey(this.widgetOrderConfirmModalInfo.security) ;

        return this.quotesStreamingService.createSubscription([securityKey]).pipe(
            takeUntil(this.destroy$),
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$
            }),
            tap((quote: Quote) => this.updatePrice(quote))
        ).subscribe();
    }

    private createOverlay(): void {
        this.overlayService.create({
            target: this.modalService.getByInstance(this)
        }).subscribe(() => this.close());
    }

    private updatePrice(quote: Quote): void {
        let displayQuote: DisplayQuote = this.displayQuote;

        if (displayQuote) {
            this.displayQuoteFactory.updateDisplayQuote(displayQuote, quote);
        } else {
            displayQuote = this.displayQuoteFactory.createDisplayQuote(quote);
        }

        this.displayQuote = displayQuote;

        if (
            this.widgetOrderConfirmModalInfo.side === SIDE_BY_SIGN.NEGATIVE
            || this.widgetOrderConfirmModalInfo.side === Side.SELL
        ) {
            this.widgetOrderConfirmModalInfo.price = displayQuote.bid.price.displayValue;
        } else {
            this.widgetOrderConfirmModalInfo.price = displayQuote.ask.price.displayValue;
        }

        this.changeDetectorRef.markForCheck();
    }

    private listenToWidgetSize(): void {
        const initialCheck$ = timer(100); // A short delay in order to allow DOM to be initialized.

        merge(
            initialCheck$,
            this.cellSizeObserverService.resize$
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => this.updateFullSizeState());
    }

    private updateFullSizeState(): void {
        const modalElement: HTMLElement = this.elementRef.nativeElement;
        const parentElement: HTMLElement = this.renderer.parentNode(this.elementRef.nativeElement);

        this.renderer.removeClass(modalElement, FULL_SIZE_CLASS);

        const modalRect = modalElement.getBoundingClientRect();
        const parentRect = parentElement.getBoundingClientRect();

        const isModalOverflowed = modalRect.width > parentRect.width || modalRect.height > parentRect.height;

        this.isFullSize = isModalOverflowed;

        if (isModalOverflowed) {
            this.renderer.addClass(modalElement, FULL_SIZE_CLASS);
        }

        this.isHidden = false;
        this.renderer.removeClass(modalElement, HIDDEN_CLASS); // Apply w/o waiting for Change Detection.

        this.changeDetectorRef.markForCheck();
    }

}
